import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import urls from "@/constants/Urls";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import Dropzone from "@/design/components/Dropzone";
import HeaderCard from "@/design/components/HeaderCard";
import Player from "@/design/components/Player";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputText from "@/design/forms/InputText";
import { updateImageStore, updateSoundStore } from "@/store/reducers/dataPbx";
import { string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import { viImage, viMusic } from "@sippy-platform/valkyrie";

import { Button, Card, Dialog, Grid, Stack, Typography } from "@mui/material";

import libraryReducer from "./libraryReducer";

function MediaDetails() {
  const { id, type, pbxId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const pbx = useSelector((state) => state.domain.pbx);

  const {
    value: file,
    dispatch: libraryDispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: {},
    reducer: libraryReducer,
    validator: () => ({
      name: string()
        .required({ message: "Name is required." })
        .length([1, 60], { message: "Name must be at most 60 characters." })
        .pattern("^[a-zA-Z0-9-_ ]*$", {
          message: "Name should only contain alphanumerical characters, dashes and underscores.",
        }),
    }),
    onSubmit: () => (type === "image" ? Api.putImage(file) : Api.putSound(file)),
    onSuccess: () => {
      Promise.resolve(type === "image" ? Api.getImages() : Api.getSounds());
    },
  });

  useEffect(() => {
    if (type === "image") {
      Promise.resolve(Api.getImage({ id: Number(id) })).then((response) =>
        libraryDispatch({ type: "set", value: response.data.image }),
      );
    } else if (type === "sound") {
      Promise.resolve(Api.getSound({ id: Number(id) })).then((response) =>
        libraryDispatch({ type: "set", value: response.data.sound }),
      );
    }
  }, [id, libraryDispatch, type]);

  // Handle changes
  const formHandler = (property, value) => {
    libraryDispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  // Delete
  function onDeleteFinish() {
    navigate(`/pbx/${pbxId}/media/library`);

    if (type === "image") {
      Promise.resolve(Api.getImages());
    } else {
      Promise.resolve(Api.getSounds());
    }
  }

  // Dropzone
  const [fileTasks, setFileTasks] = useState([]);

  const onDrop = useCallback(
    (acceptedFiles) => {
      acceptedFiles.forEach((_file) => {
        if (fileTasks.find((task) => task.file.name === _file.name)) return;

        const controller = new AbortController();

        setFileTasks((tasks) => [...tasks, { file: _file, controller, progress: {}, error: false }]);

        if (type === "sound") {
          Promise.resolve(Api.putSoundUpload(file, _file, controller, setFileTasks))
            .then((response) => {
              libraryDispatch({ type: "set", value: response.data.sound });
              setFileTasks((tasks) => tasks.filter((task) => task.file.name !== _file.name));

              dispatch(updateSoundStore(file));
            })
            .catch((error) => {
              if (error === "Upload canceled") {
                setFileTasks((tasks) => tasks.filter((task) => task.file.name !== _file.name));
              }
            });
        } else {
          Promise.resolve(Api.putImageUpload(file, _file, controller, setFileTasks))
            .then((response) => {
              libraryDispatch({ type: "set", value: response.data.image });
              setFileTasks((tasks) => tasks.filter((task) => task.file.name !== _file.name));

              dispatch(updateImageStore(file));
            })
            .catch((error) => {
              if (error === "Upload canceled") {
                setFileTasks((tasks) => tasks.filter((task) => task.file.name !== _file.name));
              }
            });
        }
      });
    },
    [dispatch, file, fileTasks, libraryDispatch, type],
  );

  return (
    <Dialog open={!!id} onClose={() => navigate(`/pbx/${pbxId}/media/library`)} maxWidth="sm">
      <HeaderCard
        avatar={<Avatar text={file.name} icon={type === "image" ? viImage : viMusic} />}
        action={
          <>
            <SaveIndicator formState={formState} errors={allErrors} save={save} />
            <DeleteFlow
              advanced
              key={file.id || "nk"}
              isActionable={!!file?.id}
              onDeleteCheck={type === "image" ? () => Api.deleteImage(file, false) : () => Api.deleteSound(file, false)}
              onDelete={type === "image" ? () => Api.deleteImage(file, true) : () => Api.deleteSound(file, true)}
              name={file.name}
              onDeleteFinish={onDeleteFinish}
            />
          </>
        }
        title={file.name || "Unnamed file"}
        subheader={type === "image" ? "Image" : "Sound"}
      />
      <Stack
        sx={{
          gap: 2,
          mt: 1,
        }}
      >
        {type === undefined ? (
          <Typography sx={{ pt: 2 }}>Pick a file to see details.</Typography>
        ) : (
          <>
            <Dropzone
              fileTasks={fileTasks}
              setFileTasks={setFileTasks}
              config={{
                onDrop,
                maxSize: 50,
                multiple: false,
                accept:
                  type === "sound"
                    ? {
                        "audio/wav": [".wav"],
                      }
                    : {
                        "image/png": [".png"],
                        "image/jpg": [".jpg"],
                      },
              }}
            />
            <FormGroup title="Info">
              <Grid size={12}>
                <InputText id="name" label="Name" onChange={formHandler} value={file.name} error={errors} />
              </Grid>
            </FormGroup>
            <FormGroup title="Preview">
              <Grid size={12}>
                {type === "sound" && (
                  <Player
                    key={file.id}
                    id={`player${file.id}`}
                    src={`${urls.audio}${pbx.context}%2F${file.id}.${file.contentType}?alt=media&token=${file.token}&ua=${file.updatedAt}`}
                  />
                )}
                {type === "image" && (
                  <Card sx={{ overflow: "hidden" }}>
                    <img
                      style={{ width: "100%", height: "auto", display: "block" }}
                      src={`${urls.image}${pbx.context}%2F${file.id}.${file.contentType}?alt=media&token=${file.token}&ua=${file.updatedAt}`}
                    />
                  </Card>
                )}
              </Grid>
            </FormGroup>
            <Stack
              direction="row"
              sx={{
                justifyContent: "flex-end",
              }}
            >
              <Button component={NavLink} to={`/pbx/${pbxId}/media/library`} variant="contained">
                Close
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    </Dialog>
  );
}

export default MediaDetails;
